import { useCallback, useMemo, useState } from 'react';
import type { Column, TableSort } from '@eon/ui';

type SortableValue = string | number | boolean | null | undefined;

function compareValues(a: SortableValue, b: SortableValue): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortTableData<T>(
  data: T[],
  sort: TableSort | null,
  columns: Column<T>[],
): T[] {
  if (!sort || data.length === 0) return data;
  const column = columns.find((c) => (c.sortKey ?? c.key) === sort.sortBy);
  if (!column?.sortable) return data;

  const getValue =
    column.sortValue ??
    ((row: T) => (row as Record<string, unknown>)[sort.sortBy] as SortableValue);

  const sorted = [...data].sort((a, b) => compareValues(getValue(a), getValue(b)));
  if (sort.sortDir === 'desc') sorted.reverse();
  return sorted;
}

/** Server-side list sorting: returns query params and table sort state. */
export function useTableSort(onPageReset?: () => void) {
  const [sort, setSort] = useState<TableSort | null>(null);

  const onSortChange = useCallback(
    (next: TableSort | null) => {
      setSort(next);
      onPageReset?.();
    },
    [onPageReset],
  );

  const sortParams = useMemo(
    () => (sort ? { sortBy: sort.sortBy, sortDir: sort.sortDir } : {}),
    [sort],
  );

  return { sort, onSortChange, sortParams };
}

/** Client-side sorting for fully loaded tables. */
export function useClientTableSort<T>(
  data: T[] | undefined,
  columns: Column<T>[],
  defaultSort?: TableSort | null,
) {
  const [sort, setSort] = useState<TableSort | null>(defaultSort ?? null);
  const sortedData = useMemo(
    () => sortTableData(data ?? [], sort, columns),
    [columns, data, sort],
  );
  const onSortChange = useCallback((next: TableSort | null) => setSort(next), []);
  return { sort, onSortChange, sortedData };
}
